import { Sort } from '@agent/core/basket/models/basket';
import { BasketVisioLabels } from '@agent/core/channel-digit/visio/models/visio.requests.interfaces';

export enum CriteriaOperator {
  TERM = 'term',
  TERMS = 'terms',
  MISSING = 'missing',
  RANGE = 'range',
  PREFIX = 'prefix',
}

export type FieldType = 'int-range' | 'date-range' | 'multi-string' | 'multi-int' | 'multi-long' | 'string' | 'int' | 'bool';

export enum FilterOperator {
  MUST = 'must',
  SHOULD = 'should',
  MUST_NOT = 'must_not',
  SHOULD_NOT = 'should_not',
  AND = 'and',
  OR = 'or',
}

export enum RangeType {
  LT = 'lt',
  GT = 'gt',
  GTE = 'gte',
  LTE = 'lte',
}

export enum WorkField {
  ANALYZED = '',
  KEYWORD = 'keyword',
}

export enum SortField {
  ORDER = 'order',
  IGNORE = 'ignore_unmapped',
}

/**
 * object fields to elastic search request
 */
export const VisioFields = {
  FIRSTNAME: 'firstname',
  AWAITING_PARTICIPANT_FIRSTNAME: 'awaitingParticipants.firstName',
  AWAITING_PARTICIPANT_LASTNAME: 'awaitingParticipants.lastName',
  AWAITING_PARTICIPANT_LOGIN: 'awaitingParticipants.lowerLogin',
  PARTICIPANT_FIRSTNAME: 'participants.lowerFirstName',
  PARTICIPANT_LASTNAME: 'participants.lowerLastName',
  PARTICIPANT_LOGIN: 'participants.lowerLogin',
  SKILLS_LIST_UUID: 'skillList.uuid',
  SKILLS_LIST_NAME: 'skillList.name',
  TARGET: 'targets',
  CSR_ID: 'csr.login',
  CSR_FIRSTNAME: 'csr.lowerFirstName',
  CSR_LASTNAME: 'csr.lowerLastName',
  CSR_LOGIN: 'csr.lowerLogin',
  START_DATE: 'startDate',
  END_DATE: 'endDate',
  TYPE: 'type',
  STATUS: 'status',
  CONTACT_FIRSTNAME: 'lowerContactFirstName',
  CONTACT_LASTNAME: 'lowerContactLastName',
  RECYCLED: 'recycled',
};

/**
 *  a criteria is an object allowing to filter. it can to have several representation :
 *     1- {CriteriaOperator : { VisioFields  :value}}  ex: {"term":{"idCsr":"kcormont"}}
 *     2- {criteriaOperator : {'field' : visioFields}}  ex: {"missing":{"field":"allTargets"}}
 *     3- a criteria can be a sub-filter
 */
export interface Criteria {
  column?: any | null;
  operator?: CriteriaOperator | null;
  workfield?: WorkField | null;
  valuesSearch?: any | null;
  filter?: Filter | null;
  range?: Range | null;
}

/**
 *  a field is composed of one or several criteria
 */
export abstract class FieldBase {
  filterOperator: FilterOperator;
  fieldType: FieldObjectType;

  protected constructor(filterOperator: FilterOperator, fieldType: FieldObjectType) {
    this.filterOperator = filterOperator;
    this.fieldType = fieldType;
  }
}

export class Field extends FieldBase {
  criteria: Criteria;

  constructor(filterOperator: FilterOperator, criteria: Criteria) {
    super(filterOperator, FieldObjectType.FIELD);
    this.criteria = criteria;
  }
}

export class FieldList extends FieldBase {
  criteriaList: Criteria[];

  constructor(filterOperator: FilterOperator, criteriaList: Criteria[]) {
    super(filterOperator, FieldObjectType.FIELDLIST);
    this.criteriaList = criteriaList;
  }
}

// export interface FieldRange extends FieldBase, Range { }

export enum FieldObjectType {
  FIELDLIST = 'fieldlist',
  FIELD = 'field',
}

export interface Range<T = any> {
  from: T;
  to: T;
  fromIncluded?: boolean;
  toIncluded?: boolean;
}

/**
 *  an filter must to have type expected that the fields  return
 */
export interface Filter {
  fields?: FieldBase[];
  type: FieldType;
}

export interface Page {
  pageSize: number;
  offset?: number;
}

export interface Query {
  bool: any;
}

export interface ELSSort {
  items: { [field: string]: { [sortField: string]: any } };
}

export interface ELSQuery {
  from: number | null;
  size: number | null;
  query: Query;
  sort?: ELSSort | null;
}

/**
 *  Allow to build an elastic search with filter query
 * @param from
 * @param size
 * @param filters
 * @param sort
 * @param range
 */
export function buildRequestsWithFilters(from: number, size: number, filters: Filter, sort: any, range?: any): ELSQuery {
  return {
    from: from,
    sort: sort,
    size: size,
    query: {
      bool: {
        must: {
          range: range == null ? { [VisioFields.END_DATE]: { [RangeType.GTE]: new Date().toISOString() } } : range,
        },
        filter: buildFilter(filters),
      },
    },
  };
}

/**
 * converted  filter object to object elastic search
 * @param filter
 * @param firstFilter
 */
export function buildFilter(filter: Filter): any {
  let fields = filter.fields;
  let type = filter.type;

  if (type == null || fields == null || fields.length < 1) return {};
  let obj2 = {};
  fields.forEach(fieldBase => {
    obj2[fieldBase.filterOperator] = buildField(fieldBase);
  });
  return { [type]: obj2 };
}

/**
 * converted  field object to object elastic search
 * @param field
 */
export function buildField(field: FieldBase) {
  switch (field.fieldType) {
    case 'fieldlist':
      let criteriaList = (field as FieldList).criteriaList;
      return buildCriteriaList(criteriaList);
    case 'field':
      let criteria = (field as Field).criteria;
      return buildCriteria(criteria);
    default:
      break;
  }
}

/**
 * converted  criteria object to object elastic search
 * @param criteria
 */
export function buildCriteria(criteria: Criteria) {
  if (criteria.filter != null) {
    return buildFilter(criteria.filter);
  } else if (criteria.range != null) {
    return {
      [criteria.column]: {
        [RangeType.GTE]: criteria.range.from,
        [RangeType.LTE]: criteria.range.to,
      },
    };
  } else if (criteria.operator === CriteriaOperator.MISSING && !!criteria.workfield && !!criteria.column) {
    return { bool: { must_not: { exists: { field: criteria.column.toString().concat('.', criteria.workfield.toString()) } } } };
  } else if (criteria.operator != null && criteria.column != null) {
    if (criteria.workfield != null) {
      return criteria.valuesSearch != null
        ? { [criteria.operator]: { [`${criteria.column}.${criteria.workfield}`]: criteria.valuesSearch } }
        : { [criteria.operator]: { field: `${criteria.column}.${criteria.workfield}` } };
    } else {
      return criteria.valuesSearch != null
        ? { [criteria.operator]: { [criteria.column]: criteria.valuesSearch } }
        : { [criteria.operator]: { field: criteria.column } };
    }
  }
  return undefined;
}

/**
 * converted  criterialist object to object elastic search
 * @param criteriaList
 */
export function buildCriteriaList(criteriaList: Criteria[]) {
  let result: any[] = [];
  for (let criteria in criteriaList) {
    let criteriaFormatted = buildCriteria(criteriaList[criteria]);
    if (criteriaFormatted !== undefined) result.push(criteriaFormatted);
  }
  return result;
}

/**
 *
 * @param csrId
 * @param sort
 * @param status
 * @param periodFilter
 * @param filterBasket
 * @param size
 * @param from
 */
export function buildPersonalRequest(
  csrId: string,
  filterBasket?: FieldList,
  periodFilter?: Criteria,
  status?: string,
  size: number = 10,
  sort?: Sort,
  from: number = 0
) {
  // build sort field
  let sortCriteria = buildRequestSort(sort);
  // build criteria period
  let periodCriteria = periodFilter != null ? buildCriteria(periodFilter) : undefined;
  // build the base request
  let criteriaCsr = createCriteria(CriteriaOperator.TERM, WorkField.KEYWORD, VisioFields.CSR_ID, csrId);
  let criteriaAppointment = createCriteria(CriteriaOperator.TERM, WorkField.KEYWORD, VisioFields.TYPE, 'APPOINTMENT');
  let fieldListMust1 = new FieldList(FilterOperator.MUST, [criteriaCsr, criteriaAppointment]);
  let criteriaFilter1 = createCriteriaWithFilter({ fields: [fieldListMust1], type: 'bool' });

  let criteriaMissingParticipant = createCriteria(CriteriaOperator.MISSING, WorkField.KEYWORD, VisioFields.PARTICIPANT_LOGIN);
  let criteriaAwaitingParticipant = createCriteria(CriteriaOperator.TERM, WorkField.KEYWORD, VisioFields.AWAITING_PARTICIPANT_LOGIN, csrId);
  let fieldListMust2 = new FieldList(FilterOperator.MUST, [criteriaMissingParticipant, criteriaAwaitingParticipant]);
  let subCriteriaFilter1 = createCriteriaWithFilter({ fields: [fieldListMust2], type: 'bool' });

  let criteriaParticipant = createCriteria(CriteriaOperator.TERM, WorkField.KEYWORD, VisioFields.PARTICIPANT_LOGIN, csrId);
  let fieldListShould = new FieldList(FilterOperator.SHOULD, [subCriteriaFilter1, criteriaParticipant]);
  let subCriteriaFilter2 = createCriteriaWithFilter({ fields: [fieldListShould], type: 'bool' });

  let criteriaInvite = createCriteria(CriteriaOperator.TERM, WorkField.KEYWORD, VisioFields.TYPE, 'INVITE');
  let fieldListMust3 = new FieldList(FilterOperator.MUST, [subCriteriaFilter2, criteriaInvite]);
  let criteriaFilter2 = createCriteriaWithFilter({ fields: [fieldListMust3], type: 'bool' });
  let fieldShould = new FieldList(FilterOperator.SHOULD, [criteriaFilter1, criteriaFilter2]);

  let criteriaRecycled = createCriteria(CriteriaOperator.TERM, undefined, VisioFields.RECYCLED, 'true');
  let fieldMustNot = new Field(FilterOperator.MUST_NOT, criteriaRecycled);

  // we apply the selected label (status)
  let fieldMust;
  let criteriaMust: any[] = [];
  if (!!status && status !== BasketVisioLabels.ALL) {
    let criteriaStatus = createCriteria(CriteriaOperator.TERM, WorkField.KEYWORD, VisioFields.STATUS, status);
    criteriaMust.push(criteriaStatus);
    fieldMust = new Field(FilterOperator.MUST, criteriaStatus);
  }
  // we apply the basket filter
  if (filterBasket != null) {
    fieldMust = new FieldList(FilterOperator.MUST, criteriaMust.concat(filterBasket.criteriaList));
  }
  let fieldBase: FieldBase[] = fieldMust != null ? [fieldShould, fieldMust, fieldMustNot] : [fieldShould, fieldMustNot];
  // we build an elastic request
  return buildRequestsWithFilters(from, size, { fields: fieldBase, type: 'bool' }, sortCriteria, periodCriteria);
}

export function buildCommonsRequests(
  teamCsr: string,
  groupCsr: string[],
  filterBasket?: FieldList,
  periodFilter?: Criteria,
  size: number = 10,
  sort?: Sort,
  from: number = 0
) {
  let teamAndGroup = [teamCsr, ...groupCsr];
  // build sort field
  let sortCriteria = buildRequestSort(sort);
  // build criteria period
  let periodCriteria = periodFilter != null ? buildCriteria(periodFilter) : undefined;
  // build the base request
  let criteriaSkill = createCriteria(CriteriaOperator.TERMS, WorkField.KEYWORD, VisioFields.SKILLS_LIST_UUID, ['UNKNOWN_SKILL']);
  let fieldMustNot = new Field(FilterOperator.MUST_NOT, criteriaSkill);

  let criteriaMissingTarget = createCriteria(CriteriaOperator.MISSING, WorkField.KEYWORD, VisioFields.TARGET);
  let criteriaTarget = createCriteria(CriteriaOperator.TERMS, WorkField.KEYWORD, VisioFields.TARGET, teamAndGroup);
  let fieldShould = new FieldList(FilterOperator.SHOULD, [criteriaMissingTarget, criteriaTarget]);

  let criteriaAppointment = createCriteria(CriteriaOperator.TERM, WorkField.KEYWORD, VisioFields.TYPE, 'APPOINTMENT');
  let criteriaRecycled = createCriteria(CriteriaOperator.TERM, undefined, VisioFields.RECYCLED, 'true');
  let fieldListMust1 = new FieldList(FilterOperator.MUST, [criteriaRecycled, criteriaAppointment]);
  let criteriaFilter1 = createCriteriaWithFilter({ fields: [fieldListMust1], type: 'bool' });

  let criteriaMissingCsrLogin = createCriteria(CriteriaOperator.MISSING, WorkField.KEYWORD, VisioFields.CSR_ID);
  let fieldListMust2 = new FieldList(FilterOperator.MUST, [criteriaMissingCsrLogin, criteriaAppointment]);
  let criteriaFilter2 = createCriteriaWithFilter({ fields: [fieldListMust2], type: 'bool' });

  let criteriaInvite = createCriteria(CriteriaOperator.TERM, WorkField.KEYWORD, VisioFields.TYPE, 'INVITE');
  let criteriaMissingParticipantLogin = createCriteria(CriteriaOperator.MISSING, WorkField.KEYWORD, VisioFields.PARTICIPANT_LOGIN);
  let criteriaMissingAwaitingLogin = createCriteria(CriteriaOperator.MISSING, WorkField.KEYWORD, VisioFields.AWAITING_PARTICIPANT_LOGIN);
  let fieldListMust3 = new FieldList(FilterOperator.MUST, [criteriaInvite, criteriaMissingParticipantLogin, criteriaMissingAwaitingLogin]);
  let criteriaFilter3 = createCriteriaWithFilter({ fields: [fieldListMust3], type: 'bool' });

  let fieldListShould = new FieldList(FilterOperator.SHOULD, [criteriaFilter1, criteriaFilter2, criteriaFilter3]);
  let criteriaFilterShould = createCriteriaWithFilter({ fields: [fieldListShould], type: 'bool' });
  // we apply the basket filter
  let fieldMust;
  if (filterBasket == null) {
    fieldMust = new Field(FilterOperator.MUST, criteriaFilterShould);
  } else {
    fieldMust = new FieldList(FilterOperator.MUST, [criteriaFilterShould].concat(filterBasket.criteriaList));
  }
  let fieldBase: FieldBase[] = [fieldShould, fieldMust, fieldMustNot];
  // we build an elastic request
  return buildRequestsWithFilters(from, size, { fields: fieldBase, type: 'bool' }, sortCriteria, periodCriteria);
}

function buildRequestSort(sort?: Sort): any {
  let sortKey = `${VisioFields.CSR_FIRSTNAME}.${WorkField.KEYWORD}`;
  if (sort != null && sort.key === VisioFields.START_DATE) {
    sortKey = sort.key;
  } else if (sort != null) {
    sortKey = sort.key.concat('.', WorkField.KEYWORD);
  }
  return {
    [sortKey]: {
      [SortField.ORDER]: sort != null ? sort.order : 'asc',
    },
  };
}

export function createCriteria(operator?: CriteriaOperator, workField?: WorkField, column?: any, valuesSearch?: any): Criteria {
  return {
    operator: operator,
    workfield: workField,
    column: column,
    valuesSearch: valuesSearch,
  };
}

export function createCriteriaWithFilter(filter?: Filter): Criteria {
  return {
    filter: filter,
  };
}
